import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Loader from '../Loader';
import _ from 'lodash';
import './style.scss';

function splitObject(obj, parts) {
  let left = {};
  let right = {};
  Object.keys(obj).forEach((k) => {
    if (parts.indexOf(k) !== -1) {
      left[k] = obj[k];
    } else {
      right[k] = obj[k];
    }
  });
  return [left, right];
}

const propTypes = {
  src: PropTypes.string.isRequired,
  fallbackImgSrc: PropTypes.string,
  placeholder: PropTypes.string,
  offset: PropTypes.object,
  loaderProps: PropTypes.object,
  minHeight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  loadOnlyOnce: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  handleError: PropTypes.func,
  className: PropTypes.string,
};

const defaultProps = {
  placeholder: '',
  offset: {
    min: 0,
    max: 0,
  },
  loaderProps: {},
  minHeight: 100,
  loadOnlyOnce: false,
  fallbackImgSrc: '',
  handleError: null,
  lazyLoad: true,
  className: '',
};

class ImageLazyLoad extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      viewport: {
        top: window.pageYOffset,
        height: window.innerHeight,
      },
      imageLoaded: false,
      activeSrc: null,
      fallbackSrc: null,
      nextSrc: null,
    };
    this.errorCache = [];
    this.handleScroll = this.handleScroll.bind(this);
    this.imageLoaded = this.imageLoaded.bind(this);
    this.imageError = this.imageError.bind(this);
    this.loadImg = this.loadImg.bind(this);
    this.unloadImg = this.unloadImg.bind(this);
    this.mounted = true;
  }

  componentWillMount() {
    this.setState({ nextSrc: this.props.src, fallbackSrc: this.props.fallbackImgSrc });
    this.mounted = true;
  }

  componentDidMount() {
    if (this.props.lazyLoad) {
      this.debouncedScrollHandler = _.debounce(this.handleScroll);
      window.addEventListener('scroll', this.debouncedScrollHandler, false);
      document.addEventListener('resize', this.debouncedScrollHandler, false);
      window.addEventListener("checkImageLazyload", function () {
        if (!this.state.visible) {
          this.checkVisible();
        }
      }.bind(this));
    }
    document.addEventListener('onload', this.imageLoaded);
    this.loadImg();
    this.checkVisible();
  }

  componentWillUnmount() {
    if (this.props.lazyLoad) {
      this.debouncedScrollHandler.cancel();
      window.removeEventListener('scroll', this.debouncedScrollHandler);
      document.removeEventListener('resize', this.debouncedScrollHandler);
    }
    if (this.i) this.unloadImg();
    this.mounted = false;
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.src !== this.props.src) {
      if(this.errorCache.includes(nextProps.src)) return;
      if (!this.props.loadOnlyOnce) {
        const visible = (this.props.lazyLoad) ? false : true;
        this.setState({ visible: visible, imageLoaded: false, nextSrc: nextProps.src }, this.loadImg);
      } else {
        this.setState({ nextSrc: nextProps.src }, this.loadImg);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.visible && prevState.viewport) {
      this.checkVisible();
    }
  }

  checkVisible() {
    if(!this.props.lazyLoad) {
      return this.setState({visible:true});
    }
    let ele = ReactDOM.findDOMNode(this.refs['imageWrap']);
    if (!ele) return;
    let rect = ele.getBoundingClientRect();

    if (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    ) {
      this.setState({
        viewport: this.state.viewport,
        visible: true,
      });
    }
    return;
  }

  handleScroll(e) {
    this.setState({
      visible: this.state.visible,
      viewport: {
        top: window.pageYOffset,
        height: window.innerHeight,
      },
    });
  }

  imageLoaded(e) {
    if (this.mounted) {
      let src = _.get(e, 'currentTarget.attributes.src.value');
      this.setState({imageLoaded: true, fallbackSrc: this.state.activeSrc, activeSrc: src});
      this.checkVisible();
    }
  }

  imageError(e) {
    if (this.mounted) {
      let src = _.get(e, 'currentTarget.attributes.src.value');
      if (src && src !== this.state.fallbackSrc) {
        this.errorCache.push(src)
      }
      if (!_.isEmpty(this.state.fallbackSrc) && !_.isNull(this.state.fallbackSrc)) {
        this.setState({activeSrc: this.state.fallbackSrc, nextSrc: null});
      }
      if (this.props.handleError) {
        this.props.handleError();
      }
    }
  }

  loadImg() {
    if (this.mounted) {
      this.i = new Image();
      this.i.src = this.state.nextSrc;
      this.i.onload = this.imageLoaded;
      this.i.onerror = this.imageError;
    }
  }

  unloadImg() {
    delete this.i.onerror;
    delete this.i.onload;
    delete this.i.src;
    delete this.i;
  }

  render() {
    if (!this.props.src) {
      console.error('ImageLazyLoad props "src" is required');
    }

    let { imageLoaded } = this.state;

    let src = this.state.visible ? this.state.activeSrc : this.props.placeholder;

    let [rest, imgProps] = splitObject(this.props, ['src', 'placeholder', 'loaderProps', 'minHeight', 'offset', 'loadOnlyOnce', 'fallbackImgSrc', 'handleError', 'lazyLoad']);

    let stateClass = (this.state.visible && this.state.imageLoaded) ? 'ready' : '';

    return (
      <div ref="imageWrap" className={`image-load-wrapper ${stateClass}`} style={{ minHeight: rest.minHeight }}>
        {(!imageLoaded || !src) && <Loader ref="loader" {...rest.loaderProps} fill />}
        {src && <img ref="img" src={src} {...imgProps} onLoad={this.imageLoaded} onError={this.imageError} />}
      </div>
    )
  }
}

ImageLazyLoad.propTypes = propTypes;
ImageLazyLoad.defaultProps = defaultProps;

export default ImageLazyLoad;
